import React, { useState, useMemo, useCallback } from 'react';
import { LanguageContext, useLanguage } from '@/components/LanguageState';
export { LanguageContext, useLanguage };

import readDanishTranslations from '@/components/danishTranslationResource';

// Preserve the legacy export without downloading its content until requested.
export const translations = { get da() { return readDanishTranslations(); } };

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState('da'); // Default, will be updated by useEffect
  const [initialized, setInitialized] = useState(false);

  // Initialize language on mount - ALWAYS Danish, English is disabled
  React.useEffect(() => {
    const initLanguage = () => {
      // Always use Danish - English is disabled
      setLang('da');
      
      // Remove any ?lang= parameter from URL if present
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('lang')) {
          urlParams.delete('lang');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
        }
      }
      setInitialized(true);
    };

    initLanguage();
  }, []);

  // Switch language and update URL
  const switchLanguage = useCallback(async (newLang) => {
    if (newLang === 'en' && !translations.en) {
      translations.en = (await import('@/components/translations/englishTranslations')).default;
    }
    setLang(newLang);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (newLang === 'da') {
        url.searchParams.delete('lang');
      } else {
        url.searchParams.set('lang', newLang);
      }
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Legacy function for backwards compatibility
  const switchToDanish = useCallback(() => {
    switchLanguage('da');
  }, [switchLanguage]);

  const t = useMemo(() => {
    return (keyPath, replacements = {}) => {
      const currentTranslations = translations[lang] || translations.da;
      // First, try direct lookup (for flattened keys like 'footer.companyName')
      if (keyPath in currentTranslations) {
        let value = currentTranslations[keyPath];
        if (typeof value === 'string') {
          return Object.entries(replacements).reduce(
            (str, [key, val]) => str.replace(`{${key}}`, val),
            value
          );
        }
        return value;
      }
      
      // Second, try nested lookup (for nested objects like home.heroTitle1)
      const keys = keyPath.split('.');
      let value = currentTranslations;
      
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          console.warn(`Translation missing: ${keyPath}`);
          return keyPath;
        }
      }
      
      if (typeof value === 'string') {
        return Object.entries(replacements).reduce(
          (str, [key, val]) => str.replace(`{${key}}`, val),
          value
        );
      }
      
      return value;
    };
  }, [lang]);

  const localePath = (path) => path;

  const value = {
    lang,
    t,
    localePath,
    switchToDanish,
    switchLanguage,
    isEnglish: lang === 'en',
    isDanish: lang === 'da',
    initialized,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default useLanguage;